'use client';
import { jsxs, jsx } from 'react/jsx-runtime';
import Link from 'next/link.js';
import { useRef, useState, useMemo, useEffect } from 'react';
import { IconChevronDown } from '../../svg/icons/icon-chevron-down.js';
import { IconChevronRight } from '../../svg/icons/icon-chevron-right.js';
import { Button } from '../button/button.js';
import { ButtonColor, ButtonSize } from '../button/constants.js';
import { HeaderEntryFontSizeClass } from './header-entry.js';

const TieredNavItem = ({ url, label, onMouseOver, selected, hasChildren, }) => {
    return url ? (jsxs(Link, { className: `flex items-center whitespace-nowrap rounded-lg px-3 py-2 ${selected
            ? 'bg-leland-gray-hover text-leland-gray-dark'
            : 'text-leland-gray-light hover:bg-leland-gray-hover hover:text-leland-gray-dark'}`, href: url, onMouseOver: onMouseOver, children: [jsx("span", { className: "grow", children: label }), jsx(IconChevronRight, { className: `ml-3 size-5 ${hasChildren ? 'text-leland-gray-light' : 'text-transparent'}` })] })) : (jsxs("button", { type: "button", className: `flex w-full items-center whitespace-nowrap rounded-lg px-3 py-2 ${selected
            ? 'bg-leland-gray-hover text-leland-gray-dark'
            : 'text-leland-gray-light hover:bg-leland-gray-hover hover:text-leland-gray-dark'}`, onMouseOver: onMouseOver, onFocus: onMouseOver, children: [jsx("span", { className: "grow text-left", children: label }), jsx(IconChevronRight, { className: `ml-2 size-5 ${hasChildren ? 'text-leland-gray-light' : 'text-transparent'}` })] }));
};
const TieredNavItemGroup = ({ items, depth, selectedGroups, setSelectedGroups, }) => {
    return (jsx("div", { children: items.map((item) => (jsx("div", { children: jsx(TieredNavItem, { ...item, selected: selectedGroups[depth + 1] === item.id, depth: depth, onMouseOver: () => {
                    setSelectedGroups((prev) => {
                        const next = prev.slice(0, depth + 1);
                        next.push(item.id);
                        return next;
                    });
                } }) }, item.id))) }));
};
const groupItems = (items) => {
    const results = [];
    const organizeByDepth = (items, depth = 0, parentId) => {
        items.forEach((item) => {
            const currId = Math.random().toString(36).substring(7);
            const group = results[depth] || { items: new Map() };
            if (!group.items.get(parentId ?? 'root')) {
                group.items.set(parentId ?? 'root', []);
            }
            group.items.get(parentId ?? 'root')?.push({
                id: currId,
                url: item.url,
                label: item.label,
                parentId: parentId,
                featured: item.featured ?? false,
                hasChildren: !!(item.subItems && item.subItems.length > 0),
            });
            results[depth] = group;
            if (item.subItems) {
                organizeByDepth(item.subItems, depth + 1, currId);
            }
        });
    };
    organizeByDepth(items);
    return results;
};
const TieredNav = ({ label, items, parentContainerRef, removeDelayOnCategoryHover, }) => {
    const containerRef = useRef(null);
    const [selectedGroups, setSelectedGroups] = useState(['root']);
    const [open, setOpen] = useState(false);
    const timer = useRef(null);
    const groups = useMemo(() => {
        return groupItems(items);
    }, [items]);
    useEffect(() => {
        const handleMouseOutside = (event) => {
            const refToUse = parentContainerRef ?? containerRef;
            if (refToUse.current &&
                !refToUse.current.contains(event.target)) {
                if (timer.current) {
                    clearTimeout(timer.current);
                    timer.current = null;
                }
                setOpen(false);
                setSelectedGroups(['root']);
            }
        };
        document.addEventListener('mouseover', handleMouseOutside);
        return () => document.removeEventListener('mouseover', handleMouseOutside);
    });
    const handleMouseOverWithDelay = () => {
        if (removeDelayOnCategoryHover) {
            setOpen(true);
        }
        else if (!timer.current) {
            timer.current = setTimeout(() => {
                setOpen(true);
            }, 250);
        }
    };
    return (jsxs("div", { className: "relative flex", ref: containerRef, children: [jsx("div", { className: HeaderEntryFontSizeClass[ButtonSize.MEDIUM], children: jsx(Button, { label: label, buttonColor: ButtonColor.REVEAL, RightIcon: IconChevronDown, onMouseOver: () => handleMouseOverWithDelay(), onFocus: () => handleMouseOverWithDelay() }) }), jsx("div", { className: `absolute left-0 top-full -mt-px rounded-lg ${open ? 'flex' : 'hidden'} border border-leland-gray-stroke bg-leland-white`, children: groups.map((group, index) => (jsx("div", { className: `${groups[index]?.items.get(selectedGroups[index])
                        ? 'flex'
                        : 'hidden'} min-w-60 flex-col border-l border-leland-gray-stroke p-2 first:border-l-0`, children: Array.from(group.items).map(([parentId, items]) => (jsx("div", { className: `${selectedGroups[index] === parentId ? 'block' : 'hidden'}`, children: jsx(TieredNavItemGroup, { items: items, depth: index, selectedGroups: selectedGroups, setSelectedGroups: setSelectedGroups }) }, parentId))) }, index))) })] }));
};

export { TieredNav, groupItems };
//# sourceMappingURL=tiered-nav.js.map
